import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  ArrowRight,
  Camera as CameraIcon,
  Download,
  Leaf,
  RefreshCw,
  Share2,
  Sparkles,
  TrendingUp,
  Trash2,
  Undo2,
  Droplet,
  Apple,
  ChefHat,
  Save,
} from 'lucide-react';

import { theme } from '../styles/theme';
import {
  iosNativeContent,
  iosNativeHeaderStack,
  iosNativePageRoot,
  iosNativeScreenSubtitle,
  iosNativeScreenTitle,
} from '../styles/iosNativePageCss';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import { useAWSAuth as useAuth } from '../contexts/AWSAuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { FeatureLock } from '../components/FeatureLock';
import { getDIYMaskCount } from '../types/subscription';
import { severityToHeadline } from '../utils/acneAnalysis';
import { getRecommendedProductsFromScan } from '../utils/productRecommendations';
import { getScanDisplayName } from '../utils/scanUtils';
import { MEDICAL_DISCLAIMER_SHORT, formatPHWithDisclaimer } from '../utils/medicalDisclaimers';
import { checkProductConflicts } from '../components/PrescriptionForm';
import { AlertTriangle } from 'lucide-react';
import { SkinIssuesDisplay } from '../components/SkinIssuesDisplay';
import {
  deleteScanAssets,
  deleteScanRecord,
  getScanRecord,
  getScanRecordByScanId,
  getSignedScanAssetUrl,
  listUserScans,
  ScanRecord,
  ScanStatus,
  updateScanRecord,
} from '../services/scanService';

/** Create minimal recipe fallback so every food shows View Recipe */
function getRecipeForDisplay(food: { name: string; description?: string; recipe?: { title?: string; ingredients?: string[]; steps?: string[]; servings?: number; caloriesPerServing?: number } }) {
  if (food.recipe && (food.recipe.ingredients?.length || food.recipe.steps?.length)) {
    return { title: food.recipe.title || food.name, ingredients: food.recipe.ingredients || [], steps: food.recipe.steps || [], servings: food.recipe.servings ?? 2, caloriesPerServing: food.recipe.caloriesPerServing };
  }
  return {
    title: food.name,
    ingredients: ['Whole grains or vegetable base', 'Fresh vegetables', 'Olive oil or healthy fat', 'Herbs and spices', 'Salt and pepper to taste'],
    steps: ['Review the description above for key ingredients.', 'Use whole grains and vegetables where possible.', 'Prepare using baking, grilling, or steaming instead of frying.', 'Season to taste and serve.'],
    servings: 2,
    caloriesPerServing: 350,
  };
}

const PageContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: linear-gradient(
    180deg,
    ${theme.colors.background.secondary} 0%,
    ${theme.colors.background.primary} 100%
  );
  padding: 2rem 1rem 4rem;

  ${iosNativePageRoot}
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  gap: 2rem;

  ${iosNativeContent}
`;

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
`;

const Header = styled.header`
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;

  @media (min-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr auto;
    align-items: center;
  }

  body.native-app & {
    grid-template-columns: 1fr;
  }

  ${iosNativeHeaderStack}
`;

const TitleGroup = styled.div`
  display: grid;
  gap: 0.5rem;
`;

const Title = styled.h1`
  margin: 0;
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: clamp(2rem, 4vw, 2.5rem);
  color: ${theme.colors.text.primary};

  ${iosNativeScreenTitle}
`;

const Subtitle = styled.p`
  margin: 0;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.sm};

  ${iosNativeScreenSubtitle}
`;

const ActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: flex-start;

  @media (min-width: ${theme.breakpoints.md}) {
    justify-content: flex-end;
  }
`;

const Grid = styled.div`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 1fr;

  @media (min-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 2fr 1fr;
  }
`;

const PhotosCard = styled(Card)`
  display: grid;
  gap: 1.5rem;
  padding: 1.5rem;
`;

const PhotoGrid = styled.div`
  display: grid;
  gap: 1rem;

  @media (min-width: ${theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  
  @media (min-width: ${theme.breakpoints.md}) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const PhotoWrapper = styled.div`
  position: relative;
  border-radius: ${theme.borderRadius['2xl']};
  overflow: hidden;
  aspect-ratio: 3 / 4;
  background: linear-gradient(135deg, ${theme.colors.glow.mint}20 0%, white 100%);
  box-shadow: ${theme.shadows.base};
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Placeholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  color: ${theme.colors.text.tertiary};
  font-size: ${theme.typography.fontSize.sm};

  svg {
    color: ${theme.colors.mint[500]};
    width: 28px;
    height: 28px;
  }
`;

const PhotoMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
`;

const ScoreCard = styled(Card)`
  padding: 1.5rem;
  background: linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.mint[500]} 100%);
  color: white;
  border: none;
  box-shadow: ${theme.shadows.lg};
  display: grid;
  gap: 1rem;
`;

const MedicalDisclaimerBox = styled.div`
  margin-top: 1.5rem;
  padding: 1rem 1.25rem;
  background: ${theme.colors.status.warning}15;
  border: 2px solid ${theme.colors.status.warning};
  border-radius: ${theme.borderRadius.lg};
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.primary};
  font-weight: ${theme.typography.fontWeight.medium};
  line-height: 1.6;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  
  svg {
    flex-shrink: 0;
    margin-top: 0.125rem;
    color: ${theme.colors.status.warning};
  }
`;

const ScoreValue = styled.div`
  font-size: 3.5rem;
  font-weight: ${theme.typography.fontWeight.extrabold};
  line-height: 1;
  letter-spacing: -0.04em;
`;

const ScoreLabel = styled.div`
  font-size: ${theme.typography.fontSize.base};
  color: rgba(255, 255, 255, 0.85);
`;

const ScoreTrend = styled.div<{ $positive?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.8rem;
  border-radius: ${theme.borderRadius.full};
  background: rgba(255, 255, 255, 0.15);
  color: ${({ $positive }) => ($positive ? '#fff' : 'rgba(255,255,255,0.9)')};
  font-size: ${theme.typography.fontSize.sm};
  width: fit-content;
`;

const InsightsGrid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;
  align-items: start;
  grid-auto-rows: min-content;

  @media (min-width: ${theme.breakpoints.md}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }
`;

const AnalysisGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  width: 100%;
  margin-bottom: 1.5rem;

  @media (min-width: ${theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${theme.breakpoints.lg}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const TwoColumnGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 1fr;
  width: 100%;
  margin-bottom: 1.5rem;

  @media (min-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 2fr 1fr;
  }
`;

const EqualTwoColumnGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 1fr;
  width: 100%;
  margin-bottom: 1.5rem;

  @media (min-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
  }
`;

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ThreeColumnRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  width: 100%;
  margin-bottom: 1.5rem;

  @media (min-width: ${theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${theme.breakpoints.lg}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const CompactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  align-items: start;
  width: 100%;
  margin-top: 1rem;

  @media (min-width: ${theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }
`;

const ResultsSecondaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  align-items: start;
  width: 100%;
  margin-top: 0.5rem;

  @media (min-width: ${theme.breakpoints.md}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }

  @media (min-width: ${theme.breakpoints.lg}) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1rem;
  }
`;

const InfoCard = styled(Card)`
  padding: 1rem !important;
  display: flex;
  flex-direction: column;
  height: auto !important;
  min-height: auto !important;
  max-height: none !important;
  align-items: flex-start;
  align-self: start;
  overflow: visible;
  flex: 0 0 auto;
  width: 100%;
`;

const CardTitle = styled.h3`
  margin: 0;
  margin-bottom: 0.5rem;
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${theme.colors.text.primary};

  svg {
    width: 18px;
    height: 18px;
    color: ${theme.colors.primary[500]};
  }
`;

const List = styled.ul`
  margin: 0;
  padding: 0 0 0 1.2rem;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  list-style: disc;

  li {
    margin: 0;
    padding: 0;
    line-height: 1.5;
  }
`;

const Snackbar = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: ${theme.colors.text.primary};
  color: white;
  padding: 0.85rem 1.25rem;
  border-radius: ${theme.borderRadius.full};
  box-shadow: ${theme.shadows.md};
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 1300;
`;

const SnackbarButton = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.primary[200]};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: pointer;
  font-size: ${theme.typography.fontSize.sm};
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
`;

const EmptyStateCard = styled(Card)`
  text-align: center;
  padding: 3rem 2rem;
  display: grid;
  gap: 1rem;
  justify-items: center;
`;

const ErrorCard = styled(Card)`
  border: 1px solid ${theme.colors.status.error};
  color: ${theme.colors.status.error};
  padding: 1.5rem;
  display: grid;
  gap: 0.75rem;
`;

const LoadingCard = styled(Card)`
  padding: 2rem;
  text-align: center;
`;

const statusBadgeVariant: Record<ScanStatus, 'primary' | 'success' | 'warning' | 'info' | 'popular' | 'destructive'> = {
  uploaded: 'info',
  processing: 'warning',
  complete: 'success',
  error: 'destructive',
  deleting: 'warning',
};

type LocationState = {
  scanTimestamp?: string;
};

const ResultsDetailPage: React.FC = () => {
  const { scanId = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { getCurrentSession, userProfile } = useAuth();
  const { checkFeature, tier } = useSubscription();

  const [scan, setScan] = useState<ScanRecord | null>(null);
  const [status, setStatus] = useState<ScanStatus>('processing');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [frontUrl, setFrontUrl] = useState<string | null>(null);
  const [sideUrl, setSideUrl] = useState<string | null>(null);
  const [leftSideUrl, setLeftSideUrl] = useState<string | null>(null);
  const [rightSideUrl, setRightSideUrl] = useState<string | null>(null);
  const [trendDelta, setTrendDelta] = useState<number | null>(null);
  const [polling, setPolling] = useState(false);
  const [deleteCountdown, setDeleteCountdown] = useState(0);
  const [showDeleteSnackbar, setShowDeleteSnackbar] = useState(false);
  const [allUserScans, setAllUserScans] = useState<ScanRecord[]>([]); // Store all scans to calculate scan number
  const deleteTimerRef = useRef<NodeJS.Timeout | null>(null);
  const deleteTickerRef = useRef<NodeJS.Timeout | null>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const navState = location.state as LocationState | null;

  const fetchScan = useCallback(
    async (fromPoll: boolean = false) => {
      if (!fromPoll) {
        setLoading(true);
      }
      setError(null);
      setNotFound(false);

      try {
        const session = await getCurrentSession();
        if (!session) {
          throw new Error('Please sign in to view this scan.');
        }

        const uid = session.getIdToken().payload.sub;

        // Fetch all user scans to calculate scan number
        try {
          const allScansResponse = await listUserScans(session, uid, 100);
          // Include all scans (not just completed) to calculate accurate scan numbers
          const sortedScans = allScansResponse.items
            .sort((a, b) => {
              const timeA = new Date(a.createdAt ?? a.timestamp).getTime();
              const timeB = new Date(b.createdAt ?? b.timestamp).getTime();
              return timeA - timeB; // Oldest first
            });
          setAllUserScans(sortedScans);
        } catch (err) {
          console.warn('Failed to fetch all scans for scan number calculation', err);
        }

        let record: ScanRecord | null = null;
        if (navState?.scanTimestamp) {
          record = await getScanRecord(session, uid, navState.scanTimestamp);
          if (record && record.scanId !== scanId) {
            record = null;
          }
        }

        if (!record) {
          record = await getScanRecordByScanId(session, uid, scanId);
        }

        if (!record) {
          setNotFound(true);
          return;
        }

        if (record.uid !== uid) {
          setNotFound(true);
          return;
        }

        setScan(record);
        setStatus(record.status);

        if (record.status === 'complete' || record.status === 'processing') {
          try {
            const urlPromises: Promise<string>[] = [
              getSignedScanAssetUrl(session, record.storageKeys.front, 120),
              getSignedScanAssetUrl(session, record.storageKeys.side, 120),
            ];
            
            // Add left and right side URLs if available
            if (record.storageKeys.leftSide) {
              urlPromises.push(getSignedScanAssetUrl(session, record.storageKeys.leftSide, 120));
            }
            if (record.storageKeys.rightSide) {
              urlPromises.push(getSignedScanAssetUrl(session, record.storageKeys.rightSide, 120));
            }
            
            const urls = await Promise.all(urlPromises);
            setFrontUrl(urls[0]);
            setSideUrl(urls[1]);
            if (record.storageKeys.leftSide && urls[2]) {
              setLeftSideUrl(urls[2]);
            }
            if (record.storageKeys.rightSide) {
              setRightSideUrl(urls[record.storageKeys.leftSide ? 3 : 2]);
            }
          } catch (assetError) {
            console.warn('Failed to fetch signed URLs', assetError);
          }
        }

        if (typeof record.glowScore === 'number') {
          try {
            const history = await listUserScans(session, uid, 6);
            const previous = history.items
              .filter((item) => item.scanId !== record!.scanId && item.status === 'complete')
              .sort(
                (a, b) =>
                  new Date(b.createdAt ?? b.timestamp).getTime() -
                  new Date(a.createdAt ?? a.timestamp).getTime()
              )[0];

            if (previous && typeof previous.glowScore === 'number') {
              setTrendDelta(record.glowScore - previous.glowScore);
            } else {
              setTrendDelta(null);
            }
          } catch (historyError: any) {
            // Check if it's a permission error
            const isPermissionError = 
              historyError?.name === 'AccessDeniedException' ||
              historyError?.code === 'AccessDeniedException' ||
              historyError?.message?.includes('not authorized') ||
              historyError?.message?.includes('dynamodb:Query') ||
              historyError?.message?.includes('no identity-based policy');
            
            if (isPermissionError) {
              // Silently handle permission errors - trend delta is optional
              // The IAM policy needs to be updated (see IAM_PERMISSIONS_FIX.md)
              console.debug('[DEBUG] DynamoDB permission error for trend delta (non-critical)', {
                error: historyError?.message,
                note: 'Trend delta feature requires dynamodb:Query permission. See IAM_PERMISSIONS_FIX.md',
              });
            } else {
              // Log other errors for debugging
              console.warn('Unable to fetch scan history for trend delta', historyError);
            }
            setTrendDelta(null);
          }
        } else {
          setTrendDelta(null);
        }
      } catch (err) {
        console.error('Failed to load scan', err);
        setError(err instanceof Error ? err.message : 'Unable to load this scan.');
      } finally {
        if (!fromPoll) {
          setLoading(false);
        }
      }
    },
    [getCurrentSession, navState?.scanTimestamp, scanId]
  );

  useEffect(() => {
    void fetchScan(false);
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [fetchScan]);

  useEffect(() => {
    if (status === 'processing') {
      setPolling(true);
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      pollingRef.current = setInterval(() => {
        void fetchScan(true);
      }, 10000);
      return () => {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
        }
      };
    }

    setPolling(false);
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
  }, [status, fetchScan]);

  const cleanupDeleteTimers = () => {
    if (deleteTimerRef.current) {
      clearTimeout(deleteTimerRef.current);
      deleteTimerRef.current = null;
    }
    if (deleteTickerRef.current) {
      clearInterval(deleteTickerRef.current);
      deleteTickerRef.current = null;
    }
  };

  useEffect(() => { return cleanupDeleteTimers; }, []);

  const handleDownload = useCallback(
    async (slot: 'front' | 'side' | 'leftSide' | 'rightSide') => {
      if (!scan) return;
      try {
        const session = await getCurrentSession();
        if (!session) {
          throw new Error('Please sign in again to download this photo.');
        }
        let key: string;
        if (slot === 'front') {
          key = scan.storageKeys.front;
        } else if (slot === 'leftSide' && scan.storageKeys.leftSide) {
          key = scan.storageKeys.leftSide;
        } else if (slot === 'rightSide' && scan.storageKeys.rightSide) {
          key = scan.storageKeys.rightSide;
        } else {
          key = scan.storageKeys.side; // Fallback to legacy side
        }
        const signed = await getSignedScanAssetUrl(session, key, 60);
        const link = document.createElement('a');
        link.href = signed;
        link.download = `glowlens-${slot}-${scan.scanId}.jpg`;
        link.click();
      } catch (err) {
        console.error('Download error', err);
        showToast(
          err instanceof Error ? err.message : 'We could not download that file.',
          'error'
        );
      }
    },
    [getCurrentSession, scan, showToast]
  );

  const handleDownloadResults = useCallback(
    async () => {
      if (!scan) return;
      try {
        const session = await getCurrentSession();
        if (!session) {
          throw new Error('Please sign in again to download results.');
        }

        // Generate HTML report content
        const scanDate = scan.createdAt 
          ? new Date(scan.createdAt).toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })
          : 'Unknown date';
        
        const scanName = scan.customName || scan.scanId || 'Scan';
        const glowScore = scan.glowScore || scan.analysis?.skinHealthIndex || 0;
        const skinHealthStatus = scan.analysis?.skinHealthStatus || 'Not determined';
        const acneSeverity = scan.analysis?.acneSeverity || scan.analysis?.acneAnalysis?.overallSeverity || 'Not detected';
        const estimatedPH = scan.analysis?.estimatedPH ? scan.analysis.estimatedPH.toFixed(1) : 'Not measured';
        const recommendations = scan.analysis?.recommendations || [];
        const progressHighlights = scan.analysis?.progressHighlights || [];
        const nextBestStep = scan.analysis?.nextBestStep || '';
        const acneAnalysis = scan.analysis?.acneAnalysis;
        const notesForUser = acneAnalysis?.notesForUser || [];
        const regions = acneAnalysis?.regions || [];

        const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>GlowLens Scan Result - ${scanName}</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      line-height: 1.6;
      color: #333;
      max-width: 800px;
      margin: 0 auto;
      padding: 2rem;
      background: #fff;
    }
    .header {
      text-align: center;
      border-bottom: 3px solid #14b8a6;
      padding-bottom: 1rem;
      margin-bottom: 2rem;
    }
    .header h1 {
      color: #14b8a6;
      margin: 0;
      font-size: 2rem;
    }
    .header p {
      color: #666;
      margin: 0.5rem 0 0 0;
    }
    .section {
      margin-bottom: 2rem;
    }
    .section h2 {
      color: #1f2937;
      border-bottom: 2px solid #e5e7eb;
      padding-bottom: 0.5rem;
      margin-bottom: 1rem;
      font-size: 1.5rem;
    }
    .stat-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 1rem;
      margin-bottom: 1.5rem;
    }
    .stat-card {
      background: #f9fafb;
      padding: 1rem;
      border-radius: 8px;
      border-left: 4px solid #14b8a6;
    }
    .stat-label {
      font-size: 0.875rem;
      color: #6b7280;
      margin-bottom: 0.25rem;
    }
    .stat-value {
      font-size: 1.5rem;
      font-weight: bold;
      color: #1f2937;
    }
    .list {
      list-style: none;
      padding: 0;
    }
    .list li {
      padding: 0.5rem 0;
      border-bottom: 1px solid #e5e7eb;
    }
    .list li:last-child {
      border-bottom: none;
    }
    .summary {
      background: #f9fafb;
      padding: 1rem;
      border-radius: 8px;
      margin-bottom: 1rem;
      line-height: 1.8;
    }
    .region-item {
      background: #f9fafb;
      padding: 1rem;
      border-radius: 8px;
      margin-bottom: 1rem;
    }
    .region-title {
      font-weight: 600;
      color: #1f2937;
      margin-bottom: 0.5rem;
    }
    .footer {
      text-align: center;
      margin-top: 3rem;
      padding-top: 2rem;
      border-top: 1px solid #e5e7eb;
      color: #6b7280;
      font-size: 0.875rem;
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>GlowLens Scan Result</h1>
    <p><strong>${scanName}</strong></p>
    <p>${scanDate}</p>
  </div>

  <div class="section">
    <h2>Overview</h2>
    <div class="stat-grid">
      <div class="stat-card">
        <div class="stat-label">GlowScore™</div>
        <div class="stat-value">${glowScore}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Acne Severity</div>
        <div class="stat-value">${acneSeverity}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Skin Health Status</div>
        <div class="stat-value">${skinHealthStatus}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Estimated pH</div>
        <div class="stat-value">${estimatedPH}</div>
      </div>
    </div>
  </div>

  ${notesForUser.length > 0 ? `
  <div class="section">
    <h2>Analysis Summary</h2>
    <div class="summary">
      ${notesForUser.map((note: string) => `<p>${note}</p>`).join('')}
    </div>
  </div>
  ` : ''}

  ${regions.length > 0 ? `
  <div class="section">
    <h2>Regional Analysis</h2>
    ${regions.map((region: any) => `
      <div class="region-item">
        <div class="region-title">${region.region || 'Unknown Region'}</div>
        <div>${region.analysis || region.summary || 'No analysis available'}</div>
      </div>
    `).join('')}
  </div>
  ` : ''}

  ${nextBestStep ? `
  <div class="section">
    <h2>Next Best Step</h2>
    <div class="summary">${nextBestStep}</div>
  </div>
  ` : ''}

  ${recommendations.length > 0 ? `
  <div class="section">
    <h2>Recommendations</h2>
    <ul class="list">
      ${recommendations.map((rec: string) => `<li>${rec}</li>`).join('')}
    </ul>
  </div>
  ` : ''}

  ${progressHighlights.length > 0 ? `
  <div class="section">
    <h2>Progress Highlights</h2>
    <ul class="list">
      ${progressHighlights.map((highlight: string) => `<li>${highlight}</li>`).join('')}
    </ul>
  </div>
  ` : ''}

  <div class="footer">
    <p>This report was generated by GlowLens AI Skincare Assistant</p>
    <p>Scan ID: ${scan.scanId}</p>
    <p>For questions or support, visit GlowLens or contact support</p>
  </div>
</body>
</html>
        `.trim();

        // Download as HTML file
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `glowlens-scan-result-${scan.scanId || scan.timestamp}.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        showToast('Scan result downloaded successfully!', 'success');
      } catch (err) {
        console.error('Download error', err);
        showToast(
          err instanceof Error ? err.message : 'We could not download the result.',
          'error'
        );
      }
    },
    [getCurrentSession, scan, showToast]
  );

  const finalizeDeletion = useCallback(
    async (session: Awaited<ReturnType<typeof getCurrentSession>>) => {
      if (!scan || !session) return;
      try {
        await deleteScanAssets(session, [
          {
            key: scan.storageKeys.front,
            thumbKey: scan.storageKeys.frontThumb,
            url: scan.front.url,
            thumbUrl: scan.front.thumbUrl,
            metadata: scan.front.metadata,
          },
          {
            key: scan.storageKeys.side,
            thumbKey: scan.storageKeys.sideThumb,
            url: scan.side.url,
            thumbUrl: scan.side.thumbUrl,
            metadata: scan.side.metadata,
          },
        ]);
        await deleteScanRecord(session, scan.uid, scan.timestamp);
        showToast('Scan deleted.', 'success');
        navigate('/results');
      } catch (err) {
        console.error('Final delete error', err);
        showToast(
          err instanceof Error
            ? err.message
            : 'We could not finish deleting this scan. Try again later.',
          'error'
        );
        setStatus('complete');
        setShowDeleteSnackbar(false);
      } finally {
        cleanupDeleteTimers();
      }
    },
    [navigate, scan, showToast, getCurrentSession]
  );

  const handleDelete = useCallback(async () => {
    if (!scan) return;
    const confirmed = window.confirm(
      'Delete this scan? You will have 10 seconds to undo.'
    );
    if (!confirmed) return;

    try {
      const session = await getCurrentSession();
      if (!session) {
        throw new Error('Session expired. Please sign in again.');
      }

      await updateScanRecord(session, scan.uid, scan.timestamp, {
        status: 'deleting',
        deleteAt: new Date().toISOString(),
      });

      setStatus('deleting');
      setShowDeleteSnackbar(true);
      setDeleteCountdown(10);

      deleteTickerRef.current = setInterval(() => {
        setDeleteCountdown((prev) => {
          if (prev <= 1) {
            if (deleteTickerRef.current) {
              clearInterval(deleteTickerRef.current);
              deleteTickerRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      deleteTimerRef.current = setTimeout(() => {
        void finalizeDeletion(session);
      }, 10000);
    } catch (err) {
      console.error('Delete error', err);
      showToast(
        err instanceof Error ? err.message : 'We could not delete this scan.',
        'error'
      );
      setStatus(scan.status);
      setShowDeleteSnackbar(false);
    }
  }, [finalizeDeletion, getCurrentSession, scan, showToast]);

  const undoDelete = useCallback(async () => {
    if (!scan) return;
    cleanupDeleteTimers();
    try {
      const session = await getCurrentSession();
      if (!session) {
        throw new Error('Session expired.');
      }

      await updateScanRecord(session, scan.uid, scan.timestamp, {
        status: 'complete',
        deleteAt: null,
      });

      setStatus('complete');
      setShowDeleteSnackbar(false);
      showToast('Deletion cancelled.', 'success');
    } catch (err) {
      console.error('Undo delete error', err);
      showToast(
        err instanceof Error ? err.message : 'We could not undo deletion.',
        'error'
      );
    }
  }, [getCurrentSession, scan, showToast]);

  const handleCopyLink = useCallback(async () => {
    const url = `${window.location.origin}/results/${scanId}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'GlowLens Result',
          text: 'Check out my GlowLens scan results',
          url,
        });
        showToast('Link shared.', 'success');
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
        showToast('Link copied to clipboard.', 'success');
      } else {
        const tempInput = document.createElement('input');
        tempInput.value = url;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
        showToast('Link copied to clipboard.', 'success');
      }
    } catch (err) {
      console.error('Share error', err);
      showToast(
        err instanceof Error ? err.message : 'We could not share that link.',
        'error'
      );
    }
  }, [scanId, showToast]);

  const scoreDescriptor = useMemo(() => {
    if (!scan || typeof scan.glowScore !== 'number') {
      return { label: 'Skin Health Index™', level: 'Analyzing…' };
    }

    // Use analysis status if available, otherwise compute from score
    if (scan.analysis?.skinHealthStatus) {
      return { label: 'Skin Health Index™', level: scan.analysis.skinHealthStatus };
    }

    // Fallback to computed status
    if (scan.glowScore >= 95) {
      return { label: 'Skin Health Index™', level: 'Elite Glow' };
    }
    if (scan.glowScore >= 85) {
      return { label: 'Skin Health Index™', level: 'Glow Mode' };
    }
    if (scan.glowScore >= 71) {
      return { label: 'Skin Health Index™', level: 'Glow Rising' };
    }
    if (scan.glowScore >= 50) {
      return { label: 'Skin Health Index™', level: 'Stable' };
    }
    if (scan.glowScore >= 35) {
      return { label: 'Skin Health Index™', level: 'Healing' };
    }
    return { label: 'Skin Health Index™', level: 'Needs Attention' };
  }, [scan]);

  const createdAtDisplay = useMemo(() => {
    if (!scan) return '';
    const createdAtDate = new Date(scan.createdAt ?? scan.timestamp);
    if (Number.isNaN(createdAtDate.getTime())) {
      return '';
    }
    return createdAtDate.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }, [scan]);

  // Calculate scan number based on chronological order
  const scanDisplayName = useMemo(() => {
    if (!scan) return 'Scan';
    if (scan.customName && scan.customName.trim()) {
      return scan.customName.trim();
    }
    // Find the scan's position in chronological order (all scans, not just completed)
    const sortedScans = allUserScans
      .sort((a, b) => {
        const timeA = new Date(a.createdAt ?? a.timestamp).getTime();
        const timeB = new Date(b.createdAt ?? b.timestamp).getTime();
        return timeA - timeB; // Oldest first
      });
    const scanIndex = sortedScans.findIndex(s => s.scanId === scan.scanId);
    const scanNumber = scanIndex >= 0 ? scanIndex + 1 : undefined;
    return getScanDisplayName(scan, scanNumber);
  }, [scan, allUserScans]);

  if (loading && !scan) {
    return (
      <PageContainer>
        <Container>
          <LoadingCard>
            <p style={{ margin: 0 }}>Loading your scan…</p>
          </LoadingCard>
        </Container>
      </PageContainer>
    );
  }

  if (notFound) {
    return (
      <PageContainer>
        <Container>
          <EmptyStateCard>
            <div style={{ fontSize: '3rem' }}>🔒</div>
            <h2>Not found / no access</h2>
            <p>You don’t have permission to view this scan or it no longer exists.</p>
            <Button onClick={() => navigate('/results')}>Back to results</Button>
          </EmptyStateCard>
        </Container>
      </PageContainer>
    );
  }

  if (error && !scan) {
    return (
      <PageContainer>
        <Container>
          <ErrorCard>
            <strong>We couldn’t load that scan.</strong>
            <p>{error}</p>
            <Button variant="outline" onClick={() => fetchScan(false)}>
              <RefreshCw size={16} />
              Try again
            </Button>
          </ErrorCard>
        </Container>
      </PageContainer>
    );
  }

  if (!scan) {
    return null;
  }

  return (
    <PageContainer>
      <main>
        <Container>
        <Breadcrumb>
          <Button variant="ghost" size="sm" onClick={() => navigate('/results')}>
            ← Results
          </Button>
          <span>/</span>
          <span>{scanDisplayName}</span>
        </Breadcrumb>

        <Header>
          <TitleGroup>
            <Title>Skin analysis</Title>
            <Subtitle>
              Taken on {createdAtDisplay || '—'} • {scanDisplayName}
            </Subtitle>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <Badge variant={statusBadgeVariant[status] ?? 'primary'}>{status}</Badge>
              {polling && (
                <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.tertiary }}>
                  Refreshing…
                </span>
              )}
            </div>
          </TitleGroup>
          <ActionsRow>
            <Button variant="outline" onClick={() => navigate('/scan')}>
              <CameraIcon size={18} />
              Retake / Replace
            </Button>
            <Button variant="outline" onClick={handleCopyLink}>
              <Share2 size={18} />
              Share / Copy Link
            </Button>
            <Button variant="outline" onClick={handleDownloadResults}>
              <Download size={18} />
              Download Result
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={status === 'deleting'}>
              <Trash2 size={18} />
              {status === 'deleting' ? 'Deleting…' : 'Delete Scan'}
            </Button>
          </ActionsRow>
        </Header>

        {error && (
          <ErrorCard>
            <strong>We had trouble loading some data.</strong>
            <p>{error}</p>
          </ErrorCard>
        )}

        <Grid>
          <PhotosCard>
            <h2 style={{ margin: 0, fontSize: theme.typography.fontSize.xl }}>Photos</h2>
            <PhotoGrid>
              <div>
                <PhotoWrapper>
                  {frontUrl ? (
                    <img src={frontUrl} alt="Front view of face scan" />
                  ) : (
                    <Placeholder>
                      <CameraIcon />
                      {status === 'processing' ? 'Processing photo…' : 'Front photo unavailable'}
                    </Placeholder>
                  )}
                </PhotoWrapper>
                <PhotoMeta>
                  <span>Front view</span>
                  <Button variant="ghost" size="sm" onClick={() => handleDownload('front')}>
                    <Download size={16} />
                    Download
                  </Button>
                </PhotoMeta>
              </div>
              {leftSideUrl && (
                <div>
                  <PhotoWrapper>
                    <img src={leftSideUrl} alt="Left side profile view of face scan" />
                  </PhotoWrapper>
                  <PhotoMeta>
                    <span>Left side</span>
                    <Button variant="ghost" size="sm" onClick={() => handleDownload('leftSide')}>
                      <Download size={16} />
                      Download
                    </Button>
                  </PhotoMeta>
                </div>
              )}
              {rightSideUrl && (
                <div>
                  <PhotoWrapper>
                    <img src={rightSideUrl} alt="Right side profile view of face scan" />
                  </PhotoWrapper>
                  <PhotoMeta>
                    <span>Right side</span>
                    <Button variant="ghost" size="sm" onClick={() => handleDownload('rightSide')}>
                      <Download size={16} />
                      Download
                    </Button>
                  </PhotoMeta>
                </div>
              )}
              {!leftSideUrl && !rightSideUrl && sideUrl && (
                <div>
                  <PhotoWrapper>
                    <img src={sideUrl} alt="Side profile view of face scan" />
                  </PhotoWrapper>
                  <PhotoMeta>
                    <span>Side view</span>
                    <Button variant="ghost" size="sm" onClick={() => handleDownload('side')}>
                      <Download size={16} />
                      Download
                    </Button>
                  </PhotoMeta>
                </div>
              )}
            </PhotoGrid>
            <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.tertiary }}>
              Captured on {createdAtDisplay}
            </div>
          </PhotosCard>

          <ScoreCard>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{scoreDescriptor.label}</span>
              <Badge variant="primary" size="sm">
                {scoreDescriptor.level}
              </Badge>
            </div>
            <ScoreValue>{typeof scan.glowScore === 'number' ? scan.glowScore : '--'}</ScoreValue>
            {trendDelta !== null && !scan.analysis?.confidenceLevel && (
              <ScoreTrend $positive={trendDelta >= 0}>
                <TrendingUp size={16} />
                {trendDelta >= 0 ? '+' : ''}
                {trendDelta.toFixed(1)} since last scan
              </ScoreTrend>
            )}
            <ScoreLabel>
              Our AI blends clarity, smoothness, tone, and breakout markers into a single index so you can track real progress.
            </ScoreLabel>
            <MedicalDisclaimerBox>
              <AlertTriangle size={20} />
              <span>{MEDICAL_DISCLAIMER_SHORT}</span>
            </MedicalDisclaimerBox>
          </ScoreCard>
        </Grid>

        {/* Second Row: Left column (Acne insights + Personalized Diet Plan) | Right column (Recommended Skincare Products + Recommended DIY Face Masks) */}
        <EqualTwoColumnGrid>
          {/* LEFT COLUMN: Acne Insights + Personalized Diet Plan */}
          <ColumnContainer>
            <InfoCard>
              <CardTitle>
                <Sparkles aria-hidden="true" />
                Acne insights
              </CardTitle>
            {status === 'processing' || !scan.analysis ? (
              <p style={{ margin: 0, color: theme.colors.text.secondary }}>
                Analyzing your photos...
              </p>
            ) : scan.analysis.acneAnalysis ? (
              <>
                {/* Overall Severity Badge */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <Badge
                    variant={
                      scan.analysis.acneAnalysis.overallSeverity === 'none' ||
                      scan.analysis.acneAnalysis.overallSeverity === 'very_mild'
                        ? 'success'
                        : scan.analysis.acneAnalysis.overallSeverity === 'mild' ||
                          scan.analysis.acneAnalysis.overallSeverity === 'mild_to_moderate'
                        ? 'warning'
                        : 'destructive'
                    }
                    size="md"
                  >
                    {scan.analysis.acneAnalysis.overallSeverity === 'none'
                      ? 'No visible acne'
                      : scan.analysis.acneAnalysis.overallSeverity === 'very_mild'
                      ? 'Very mild acne'
                      : scan.analysis.acneAnalysis.overallSeverity === 'mild'
                      ? 'Mild acne'
                      : scan.analysis.acneAnalysis.overallSeverity === 'mild_to_moderate'
                      ? 'Mild to moderate acne'
                      : scan.analysis.acneAnalysis.overallSeverity === 'moderate'
                      ? 'Moderate acne'
                      : scan.analysis.acneAnalysis.overallSeverity === 'moderate_to_severe'
                      ? 'Moderate to severe acne'
                      : 'Severe acne'}
                  </Badge>
                </div>

                {/* User-facing headline - use severityToHeadline function */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <p
                    style={{
                      margin: '0.25rem 0',
                      fontSize: theme.typography.fontSize.base,
                      fontWeight: theme.typography.fontWeight.medium,
                      color: theme.colors.text.primary,
                      lineHeight: 1.5,
                    }}
                  >
                    {severityToHeadline(
                      scan.analysis.acneAnalysis.overallSeverity,
                      scan.analysis.acneAnalysis.regions
                    )}
                  </p>
                </div>

                {/* User-facing summary bullets */}
                {scan.analysis.acneAnalysis.notesForUser.length > 0 && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    {scan.analysis.acneAnalysis.notesForUser.map((note, idx) => (
                      <p
                        key={idx}
                        style={{
                          margin: '0.25rem 0',
                          fontSize: theme.typography.fontSize.sm,
                          color: theme.colors.text.secondary,
                          lineHeight: 1.5,
                        }}
                      >
                        {note}
                      </p>
                    ))}
                  </div>
                )}

                {/* Acne Type Breakdown */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <h4
                    style={{
                      fontSize: theme.typography.fontSize.base,
                      fontWeight: theme.typography.fontWeight.semibold,
                      marginBottom: '0.375rem',
                      color: theme.colors.text.primary,
                    }}
                  >
                    Acne type breakdown:
                  </h4>
                  <List>
                    {scan.analysis.acneAnalysis.acneType.inflammatory !== 'none' && (
                      <li>
                        Inflammatory acne:{' '}
                        <strong>
                          {(() => {
                            // Cap inflammatory type based on overall severity (never show "pronounced" if severity is below moderate_to_severe)
                            let inflammatoryType = scan.analysis.acneAnalysis.acneType.inflammatory;
                            if (
                              inflammatoryType === 'pronounced' &&
                              scan.analysis.acneAnalysis.overallSeverity !== 'moderate_to_severe' &&
                              scan.analysis.acneAnalysis.overallSeverity !== 'severe'
                            ) {
                              inflammatoryType = 'moderate';
                            }
                            return inflammatoryType;
                          })()}
                        </strong>
                      </li>
                    )}
                    {scan.analysis.acneAnalysis.acneType.comedonal !== 'none' && (
                      <li>
                        Comedonal congestion:{' '}
                        <strong>
                          {scan.analysis.acneAnalysis.acneType.comedonal}
                        </strong>
                      </li>
                    )}
                    {scan.analysis.acneAnalysis.acneType.nodularLike !== 'none' && (
                      <li>
                        Larger lesions:{' '}
                        <strong>
                          {scan.analysis.acneAnalysis.acneType.nodularLike === 'suspected'
                            ? 'suspected'
                            : 'present'}
                        </strong>
                      </li>
                    )}
                  </List>
                </div>

                {/* Region Summary */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <h4
                    style={{
                      fontSize: theme.typography.fontSize.base,
                      fontWeight: theme.typography.fontWeight.semibold,
                      marginBottom: '0.375rem',
                      color: theme.colors.text.primary,
                    }}
                  >
                    Region analysis:
                  </h4>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                    }}
                  >
                    {scan.analysis.acneAnalysis.regions
                      .filter((r) => r.severity !== 'none' && r.severity !== 'very_mild')
                      .map((region) => {
                        const regionNameMap: Record<string, string> = {
                          left_cheek: 'Left cheek',
                          right_cheek: 'Right cheek',
                          forehead: 'Forehead',
                          nose: 'Nose',
                          chin: 'Chin',
                          jawline: 'Jawline',
                        };
                        return (
                          <div
                            key={region.region}
                            style={{
                              padding: '0.625rem',
                              background: theme.colors.background.secondary,
                              borderRadius: theme.borderRadius.md,
                              fontSize: theme.typography.fontSize.sm,
                            }}
                          >
                            <div
                              style={{
                                fontWeight: theme.typography.fontWeight.semibold,
                                marginBottom: '0.25rem',
                                color: theme.colors.text.primary,
                              }}
                            >
                              {regionNameMap[region.region] || region.region} —{' '}
                              {region.severity === 'mild'
                                ? 'mild'
                                : region.severity === 'mild_to_moderate'
                                ? 'mild to moderate'
                                : region.severity === 'moderate'
                                ? 'moderate'
                                : region.severity === 'moderate_to_severe'
                                ? 'moderate to severe'
                                : 'severe'}
                            </div>
                            <div
                              style={{
                                color: theme.colors.text.secondary,
                                fontSize: theme.typography.fontSize.xs,
                              }}
                            >
                              {region.activeLesionCount > 0 &&
                                `${region.activeLesionCount} active ${region.activeLesionCount === 1 ? 'pimple' : 'pimples'}`}
                              {region.activeLesionCount > 0 && region.comedoneCount > 0 && ' • '}
                              {region.comedoneCount > 0 &&
                                `${region.comedoneCount} ${region.comedoneCount === 1 ? 'clogged pore' : 'clogged pores'}`}
                              {region.hyperpigmentationLevel !== 'none' && ' • '}
                              {region.hyperpigmentationLevel !== 'none' &&
                                `${region.hyperpigmentationLevel} healing marks`}
                            </div>
                            <div
                              style={{
                                color: theme.colors.text.secondary,
                                fontSize: theme.typography.fontSize.xs,
                                marginTop: '0.25rem',
                                fontStyle: 'italic',
                              }}
                            >
                              {region.textureDescription}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* AI Detection Zone Counts (YOLOv8) */}
                {(scan.analysis as any).yoloDetection && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <h4
                      style={{
                        fontSize: theme.typography.fontSize.base,
                        fontWeight: theme.typography.fontWeight.semibold,
                        marginBottom: '0.375rem',
                        color: theme.colors.text.primary,
                      }}
                    >
                      AI spot detection:
                    </h4>
                    <div style={{
                      padding: '0.625rem',
                      background: theme.colors.background.secondary,
                      borderRadius: theme.borderRadius.md,
                      fontSize: theme.typography.fontSize.sm,
                      marginBottom: '0.5rem',
                    }}>
                      <div style={{ fontWeight: theme.typography.fontWeight.semibold, marginBottom: '0.375rem', color: theme.colors.text.primary }}>
                        {(scan.analysis as any).yoloDetection.totalDetections} spots detected
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.25rem' }}>
                        {Object.entries((scan.analysis as any).yoloDetection.zoneCounts as Record<string, number>)
                          .filter(([, count]) => (count as number) > 0)
                          .map(([zone, count]) => {
                            const zoneLabels: Record<string, string> = {
                              forehead: 'Forehead', nose: 'Nose',
                              left_cheek: 'Left cheek', right_cheek: 'Right cheek',
                              chin: 'Chin', jawline: 'Jawline', other: 'Other',
                            };
                            return (
                              <div key={zone} style={{ color: theme.colors.text.secondary, fontSize: theme.typography.fontSize.xs }}>
                                {zoneLabels[zone] || zone}: <strong>{count as number}</strong>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Texture Summary */}
                {scan.analysis.acneAnalysis.textureSummary && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <h4
                      style={{
                        fontSize: theme.typography.fontSize.base,
                        fontWeight: theme.typography.fontWeight.semibold,
                        marginBottom: '0.375rem',
                        color: theme.colors.text.primary,
                      }}
                    >
                      Texture summary:
                    </h4>
                    <p
                      style={{
                        fontSize: theme.typography.fontSize.sm,
                        color: theme.colors.text.secondary,
                        lineHeight: 1.5,
                        margin: 0,
                      }}
                    >
                      {scan.analysis.acneAnalysis.textureSummary}
                    </p>
                  </div>
                )}

                {/* pH */}
                {scan.analysis.acneAnalysis.estimatedPH && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <p style={{ margin: 0, fontSize: theme.typography.fontSize.sm }}>
                      {formatPHWithDisclaimer(scan.analysis.acneAnalysis.estimatedPH)}
                      {scan.analysis.acneAnalysis.estimatedPH >= 4.7 &&
                      scan.analysis.acneAnalysis.estimatedPH <= 5.9 ? (
                        <span
                          style={{
                            color: theme.colors.status.success,
                            marginLeft: '0.5rem',
                          }}
                        >
                          (within normal range 4.7–5.9)
                        </span>
                      ) : (
                        <span
                          style={{
                            color: theme.colors.status.warning,
                            marginLeft: '0.5rem',
                          }}
                        >
                          {scan.analysis.acneAnalysis.estimatedPH < 4.7
                            ? '(slightly below normal range)'
                            : '(slightly above normal range)'}
                        </span>
                      )}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <List>
                <li>
                  Severity: <strong>{scan.analysis.acneSeverity}</strong>
                </li>
                <li>
                  Focus area: <strong>{scan.analysis.acneFocusArea}</strong>
                </li>
                <li>Texture: {scan.analysis.textureNotes}</li>
                {scan.analysis.estimatedPH && (
                  <li>
                    {formatPHWithDisclaimer(scan.analysis.estimatedPH)}
                    {scan.analysis.estimatedPH >= 4.7 && scan.analysis.estimatedPH <= 5.9 ? (
                      <span
                        style={{ color: theme.colors.status.success, marginLeft: '0.5rem' }}
                      >
                        (within normal range 4.7–5.9)
                      </span>
                    ) : (
                      <span
                        style={{ color: theme.colors.status.warning, marginLeft: '0.5rem' }}
                      >
                        {scan.analysis.estimatedPH < 4.7
                          ? '(slightly below normal range)'
                          : '(slightly above normal range)'}
                      </span>
                    )}
                  </li>
                )}
              </List>
            )}
            {scan.analysis?.detectedSkinIssues && scan.analysis.detectedSkinIssues.length > 0 && (
              <SkinIssuesDisplay
                detectedIssueIds={scan.analysis.detectedSkinIssues}
                currentPlanTier={(userProfile?.subscriptionTier as 'plus' | 'pro' | 'elite') || 'plus'}
                onUpgrade={() => navigate('/pricing')}
                embedded
              />
            )}
          </InfoCard>

          {/* Personalized Diet Plan - Right below Acne Insights */}
          {scan.analysis?.dietPlan && (
            <InfoCard>
              {!checkFeature('customDietPlan') ? (
                <FeatureLock
                  feature="Custom Diet Plan"
                  requiredTier="pro"
                  message="Personalized diet plans based on your scan are available in GlowLens Pro."
                />
              ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '1rem' }}>
                  <CardTitle>
                    <Apple aria-hidden="true" />
                    Personalized Diet Plan
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const dietPlan = scan.analysis?.dietPlan;
                      if (dietPlan && userProfile?.uid) {
                        const savedPlans = JSON.parse(localStorage.getItem(`savedDietPlans_${userProfile.uid}`) || '[]');
                        savedPlans.push({
                          ...dietPlan,
                          scanId: scan.scanId,
                          savedAt: new Date().toISOString(),
                          scanAnalysis: scan.analysis, // Include scan analysis for "Show More Foods" functionality
                          scanCustomName: scan.customName, // Include custom scan name
                        });
                        localStorage.setItem(`savedDietPlans_${userProfile.uid}`, JSON.stringify(savedPlans));
                        showToast('Diet plan saved to Diet tab!', 'success');
                      }
                    }}
                  >
                    <Save size={16} />
                    Save to Diet Tab
                  </Button>
                </div>

                {/* Water Intake */}
                <div style={{ marginBottom: '1.5rem', padding: '1rem', background: theme.colors.background.secondary, borderRadius: theme.borderRadius.md }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <Droplet size={20} color={theme.colors.primary[500]} />
                    <h4 style={{ margin: 0, fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.semibold }}>
                      Recommended Daily Water Intake
                    </h4>
                  </div>
                  <p style={{ margin: 0, fontSize: theme.typography.fontSize.xl, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.primary[600] }}>
                    {scan.analysis.dietPlan.waterIntakeDisplay || `${scan.analysis.dietPlan.waterIntakeGlasses} glasses`} per day
                  </p>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                    {scan.analysis.dietPlan.waterIntakeSkinNote || 'Staying hydrated helps flush toxins, maintain skin elasticity, and support overall skin health.'}
                  </p>
                </div>

                {/* Foods to Eat */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.status.success }}>
                    Foods to Eat
                  </h4>
                  
                  {/* Display 3 main meals and 3 main snacks upfront - personalized to skin/acne type */}
                  {(() => {
                    // Filter meals and snacks by type - prioritize type field over calories
                    const allMeals = scan.analysis.dietPlan.foodsToEat.filter(food => {
                      // If type is explicitly set, use it
                      if (food.type === 'meal') return true;
                      if (food.type === 'snack') return false;
                      // If type is not set, use calories as fallback (meals >= 250 cal, snacks < 250 cal)
                      // But also check for meal-like names
                      const name = food.name.toLowerCase();
                      const isMealByName = name.includes('salad') && (name.includes('shrimp') || name.includes('chicken') || name.includes('salmon') || name.includes('turkey') || name.includes('beef')) ||
                                     name.includes('curry') || name.includes('stir-fry') || name.includes('bowl') || 
                                     name.includes('wrap') || name.includes('sandwich') || name.includes('burger') ||
                                     name.includes('pasta') || name.includes('rice') || name.includes('quinoa') ||
                                     name.includes('biryani') || name.includes('tikka') || name.includes('masala');
                      const calories = food.recipe?.caloriesPerServing ?? 0;
                      return isMealByName || calories >= 250;
                    });
                    
                    const allSnacks = scan.analysis.dietPlan.foodsToEat.filter(food => {
                      // If type is explicitly set, use it
                      if (food.type === 'snack') return true;
                      if (food.type === 'meal') return false;
                      // If type is not set, use calories as fallback
                      const calories = food.recipe?.caloriesPerServing ?? 0;
                      return calories > 0 && calories < 250;
                    });
                    
                    // Use the filtered lists
                    const meals = allMeals;
                    const snacks = allSnacks;
                    
                    // Select first 3 main meals (already personalized based on skin/acne type from diet plan generator)
                    const upfrontMeals = meals.slice(0, 3);
                    
                    // Select first 3 main snacks (already personalized based on skin/acne type)
                    const upfrontSnacks = snacks.slice(0, 3);
                    
                    // Combine meals and snacks for display (meals first, then snacks)
                    const upfrontFoods = [...upfrontMeals, ...upfrontSnacks];
                    
                    // Get remaining foods (excluding the ones shown upfront)
                    const shownFoodIds = new Set(upfrontFoods.map(f => f.name));
                    const remainingFoods = scan.analysis.dietPlan.foodsToEat.filter(food => !shownFoodIds.has(food.name));
                    
                    // Only show if we have at least some foods
                    if (upfrontFoods.length === 0) {
                      return (
                        <p style={{ margin: 0, fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                          No foods available. Please upload a scan to get personalized recommendations.
                        </p>
                      );
                    }
                    
                    return (
                      <>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
                          {/* Main Foods Section */}
                          {upfrontMeals.length > 0 && (
                            <>
                              <h5 style={{ margin: '0 0 0.75rem 0', fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.text.primary }}>
                                Main Foods
                              </h5>
                              {upfrontMeals.map((food, idx) => (
                                <div 
                                  key={`meal-${idx}`} 
                                  style={{ 
                                    padding: '1rem', 
                                    background: theme.colors.background.secondary, 
                                    borderRadius: theme.borderRadius.md,
                                    border: `1px solid ${theme.colors.border.light}`
                                  }}
                                >
                                  <h5 style={{ margin: '0 0 0.5rem 0', fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.medium }}>
                                    {food.name}
                                  </h5>
                                  <p style={{ margin: '0 0 0.75rem 0', fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                                    {food.description}
                                  </p>
                                  {(() => {
                                    const recipe = getRecipeForDisplay(food);
                                    return (
                                      <details style={{ marginTop: '0.5rem' }}>
                                        <summary style={{ cursor: 'pointer', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, color: theme.colors.primary[600], display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                          <ChefHat size={16} />
                                          View Recipe: {recipe.title}
                                          {recipe.caloriesPerServing && ` (${recipe.caloriesPerServing} cal/serving)`}
                                        </summary>
                                        <div style={{ marginTop: '0.75rem', paddingLeft: '1rem' }}>
                                          {recipe.servings && (
                                            <p style={{ margin: '0 0 0.5rem 0', fontSize: theme.typography.fontSize.xs, color: theme.colors.text.tertiary }}>
                                              Servings: {recipe.servings}
                                            </p>
                                          )}
                                          <div style={{ marginBottom: '0.75rem' }}>
                                            <strong style={{ fontSize: theme.typography.fontSize.sm }}>Ingredients:</strong>
                                            <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem', fontSize: theme.typography.fontSize.sm }}>
                                              {(recipe.ingredients || []).map((ing, i) => (
                                                <li key={i}>{typeof ing === 'string' ? ing : String(ing)}</li>
                                              ))}
                                            </ul>
                                          </div>
                                          <div>
                                            <strong style={{ fontSize: theme.typography.fontSize.sm }}>Steps:</strong>
                                            <ol style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem', fontSize: theme.typography.fontSize.sm }}>
                                              {(recipe.steps || []).map((step, i) => (
                                                <li key={i} style={{ marginBottom: '0.25rem' }}>{typeof step === 'string' ? step : String(step)}</li>
                                              ))}
                                            </ol>
                                          </div>
                                        </div>
                                      </details>
                                    );
                                  })()}
                                </div>
                              ))}
                            </>
                          )}
                          
                          {/* Snacks Section */}
                          {upfrontSnacks.length > 0 && (
                            <>
                              <h5 style={{ margin: '1.5rem 0 0.75rem 0', fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.text.primary }}>
                                Snacks
                              </h5>
                              {upfrontSnacks.map((food, idx) => (
                                <div 
                                  key={`snack-${idx}`} 
                                  style={{ 
                                    padding: '1rem', 
                                    background: theme.colors.background.secondary, 
                                    borderRadius: theme.borderRadius.md,
                                    border: `1px solid ${theme.colors.border.light}`
                                  }}
                                >
                                  <h5 style={{ margin: '0 0 0.5rem 0', fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.medium }}>
                                    {food.name}
                                  </h5>
                                  <p style={{ margin: '0 0 0.75rem 0', fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                                    {food.description}
                                  </p>
                                  {(() => {
                                    const recipe = getRecipeForDisplay(food);
                                    return (
                                      <details style={{ marginTop: '0.5rem' }}>
                                        <summary style={{ cursor: 'pointer', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, color: theme.colors.primary[600], display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                          <ChefHat size={16} />
                                          View Recipe: {recipe.title}
                                          {recipe.caloriesPerServing && ` (${recipe.caloriesPerServing} cal/serving)`}
                                        </summary>
                                        <div style={{ marginTop: '0.75rem', paddingLeft: '1rem' }}>
                                          {recipe.servings && (
                                            <p style={{ margin: '0 0 0.5rem 0', fontSize: theme.typography.fontSize.xs, color: theme.colors.text.tertiary }}>
                                              Servings: {recipe.servings}
                                            </p>
                                          )}
                                          <div style={{ marginBottom: '0.75rem' }}>
                                            <strong style={{ fontSize: theme.typography.fontSize.sm }}>Ingredients:</strong>
                                            <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem', fontSize: theme.typography.fontSize.sm }}>
                                              {(recipe.ingredients || []).map((ing, i) => (
                                                <li key={i}>{typeof ing === 'string' ? ing : String(ing)}</li>
                                              ))}
                                            </ul>
                                          </div>
                                          <div>
                                            <strong style={{ fontSize: theme.typography.fontSize.sm }}>Steps:</strong>
                                            <ol style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem', fontSize: theme.typography.fontSize.sm }}>
                                              {(recipe.steps || []).map((step, i) => (
                                                <li key={i} style={{ marginBottom: '0.25rem' }}>{typeof step === 'string' ? step : String(step)}</li>
                                              ))}
                                            </ol>
                                          </div>
                                        </div>
                                      </details>
                                    );
                                  })()}
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                        
                        {/* Show More Foods Button - Only for Pro+ plans */}
                        {remainingFoods.length > 0 && (tier === 'pro' || tier === 'elite') && (
                          <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                if (scan.analysis?.dietPlan) {
                                  // Pass only the remaining foods (excluding upfront ones) to avoid duplicates
                                  navigate('/diet-plan-foods', {
                                    state: { 
                                      dietPlan: {
                                        ...scan.analysis.dietPlan,
                                        foodsToEat: remainingFoods
                                      },
                                      scanAnalysis: scan.analysis,
                                      upfrontFoods: upfrontFoods // Pass upfront foods so detail page knows what was already shown
                                    }
                                  });
                                }
                              }}
                            >
                              Show More Foods ({remainingFoods.length} more)
                              <ArrowRight size={16} style={{ marginLeft: '0.5rem' }} />
                            </Button>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Foods to Avoid */}
                <div>
                  <h4 style={{ margin: '0 0 0.75rem 0', fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.status.error }}>
                    Foods to Avoid
                  </h4>
                  {scan.analysis.dietPlan.foodsToAvoid.map((food, idx) => (
                    <div key={idx} style={{ marginBottom: '0.75rem', padding: '0.75rem', background: theme.colors.background.secondary, borderRadius: theme.borderRadius.md }}>
                      <h5 style={{ margin: '0 0 0.25rem 0', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium }}>
                        {food.name}
                      </h5>
                      <p style={{ margin: 0, fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>
                        {food.reason}
                      </p>
                    </div>
                  ))}
                </div>
              </>
              )}
            </InfoCard>
          )}
          </ColumnContainer>

          {/* RIGHT COLUMN: Recommended Skincare Products + Recommended DIY Face Masks */}
          <ColumnContainer>
            {/* PRODUCT RECOMMENDATIONS SECTION */}
            {scan.analysis ? (() => {
              const userGender = userProfile?.gender;
              const recommendedProducts = getRecommendedProductsFromScan(scan, 6, userGender);
              
              // Helper function to extract product type from category
              const getProductType = (category: string): string => {
                const lower = category.toLowerCase();
                if (lower.includes('sunscreen') || lower.includes('spf')) return 'Sunscreen';
                if (lower.includes('cleanser') || lower.includes('wash')) return 'Cleanser';
                if (lower.includes('serum')) return 'Serum';
                if (lower.includes('toner') || lower.includes('essence')) return 'Toner/Essence';
                if (lower.includes('moisturizer') || lower.includes('cream')) return 'Moisturizer';
                if (lower.includes('exfoliant') || lower.includes('exfoliating')) return 'Exfoliant';
                if (lower.includes('patch') || lower.includes('patch')) return 'Spot Treatment';
                if (lower.includes('eye')) return 'Eye Care';
                return 'Treatment';
              };
              
              if (recommendedProducts.length === 0) return null;
              
              return (
                <InfoCard>
                  <CardTitle>
                    <Sparkles aria-hidden="true" />
                    Recommended Skincare Products
                  </CardTitle>
                  <p style={{ margin: '0 0 1.5rem 0', fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                    Products tailored to your skin analysis, including Korean beauty favorites
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
                    {recommendedProducts.map((product) => {
                      const productType = getProductType(product.productCategory);
                      return (
                      <div
                        key={product.id}
                        style={{
                          padding: '1rem',
                          background: theme.colors.background.secondary,
                          borderRadius: theme.borderRadius.md,
                          border: `1px solid ${theme.colors.border.light}`,
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                              <h4 style={{ margin: 0, fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.text.primary, flex: 1, minWidth: 0 }}>
                                {product.brand} {product.productCategory}
                              </h4>
                              {product.price && (
                                <div style={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.primary[600] }}>
                                  {product.price}
                                </div>
                              )}
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem', alignItems: 'center' }}>
                              <Badge size="sm" variant="info" style={{ backgroundColor: theme.colors.primary[100], color: theme.colors.primary[700], border: `1px solid ${theme.colors.primary[300]}` }}>
                                {productType}
                              </Badge>
                              {product.tags.map(tag => (
                                <Badge key={tag} size="sm" variant={tag === 'k-beauty' ? 'info' : undefined}>
                                  {tag === 'k-beauty' ? '🇰🇷 K-Beauty' : tag.replace('-', ' ')}
                                </Badge>
                              ))}
                              <Badge size="sm">
                                {product.priceRange}
                              </Badge>
                            </div>
                          </div>
                        </div>
                    <div style={{ marginTop: '0.75rem' }}>
                      <p style={{ margin: '0 0 0.5rem 0', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, color: theme.colors.text.primary }}>
                        Key Ingredients:
                      </p>
                      <p style={{ margin: '0 0 0.75rem 0', fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary, lineHeight: 1.6 }}>
                        {product.keyIngredients.join(' • ')}
                      </p>
                      <p style={{ margin: 0, fontSize: theme.typography.fontSize.xs, color: theme.colors.text.tertiary, lineHeight: 1.5, fontStyle: 'italic' }}>
                        {product.ingredientBenefits}
                      </p>
                      
                      {/* Prescription Conflict Warnings - All Plans */}
                      {userProfile && (userProfile as any).prescriptions && (userProfile as any).prescriptions.length > 0 && (() => {
                        const conflicts = checkProductConflicts(
                          product.keyIngredients,
                          (userProfile as any).prescriptions
                        );
                        if (conflicts.length === 0) return null;
                        return (
                          <div style={{ 
                            marginTop: '0.75rem', 
                            padding: '0.75rem', 
                            background: `${theme.colors.status.warning}10`,
                            border: `1px solid ${theme.colors.status.warning}30`,
                            borderRadius: theme.borderRadius.md,
                            display: 'flex',
                            gap: '0.5rem',
                            alignItems: 'flex-start'
                          }}>
                            <AlertTriangle size={18} color={theme.colors.status.warning} style={{ flexShrink: 0, marginTop: '2px' }} />
                            <div style={{ flex: 1 }}>
                              <p style={{ margin: '0 0 0.25rem 0', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.status.warning }}>
                                Prescription Conflict Warning
                              </p>
                              <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: theme.typography.fontSize.xs, color: theme.colors.text.primary }}>
                                {conflicts.map((conflict: string, idx: number) => (
                                  <li key={idx} style={{ marginBottom: '0.25rem' }}>{conflict}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                      </div>
                    );
                    })}
              </div>
              
              {/* Skincare Routine Order Note */}
              <div style={{ 
                marginTop: '1.5rem', 
                padding: '1rem', 
                background: theme.colors.glow.mint, 
                borderRadius: theme.borderRadius.md,
                border: `1px solid ${theme.colors.primary[200]}`,
              }}>
                <p style={{ 
                  margin: '0 0 0.5rem 0', 
                  fontSize: theme.typography.fontSize.sm, 
                  fontWeight: theme.typography.fontWeight.semibold, 
                  color: theme.colors.text.primary 
                }}>
                  💡 Skincare Routine Order:
                </p>
                <p style={{ 
                  margin: 0, 
                  fontSize: theme.typography.fontSize.xs, 
                  color: theme.colors.text.secondary, 
                  lineHeight: 1.6 
                }}>
                  <strong>Morning:</strong> Cleanser → Toner/Essence → Serum → Moisturizer → Sunscreen<br/>
                  <strong>Evening:</strong> Cleanser → Toner/Essence → Exfoliant (2-3x/week) → Serum → Moisturizer → Spot Treatment (if needed)
                </p>
              </div>
              
              {checkFeature('diyMaskLibraryFull') && (
                <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                  <Button
                    variant="outline"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate('/products', { state: { fromResults: true } });
                    }}
                  >
                    View All Products
                    <ArrowRight size={16} />
                  </Button>
                </div>
              )}
              </InfoCard>
              );
            })() : null}

            {/* DIY MASKS SECTION - Right below Recommended Skincare Products */}
            {scan.analysis?.diyMasks && scan.analysis.diyMasks.length > 0 && (
              <InfoCard>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <CardTitle>
                    <Sparkles aria-hidden="true" />
                    Recommended DIY Face Masks
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      const masks = scan.analysis?.diyMasks;
                      if (masks && scan.analysis && userProfile?.uid) {
                        let allMasks: typeof masks;
                        
                        // Import the function to generate additional masks
                        const { generateAdditionalDIYMasks } = await import('../utils/diyMaskGenerator');
                        
                        if (tier === 'plus') {
                          // For Plus: Save 5 upfront masks + 5 additional masks = 10 total
                          const upfrontMasks = masks.slice(0, 5); // First 5 masks shown upfront
                          const additionalMasks = generateAdditionalDIYMasks(
                            scan.analysis.skinHealthIndex,
                            scan.analysis.acneAnalysis?.overallSeverity || 'mild',
                            scan.analysis.acneAnalysis?.estimatedPH ?? undefined,
                            scan.analysis.acneAnalysis?.acneType?.inflammatory === 'moderate' || 
                            scan.analysis.acneAnalysis?.acneType?.inflammatory === 'pronounced'
                          ).slice(0, 5); // 5 additional masks
                          allMasks = [...upfrontMasks, ...additionalMasks]; // Total: 10 masks
                        } else if (tier === 'pro' || tier === 'elite') {
                          // For Pro+: Save 5 upfront masks + 8 additional masks = 13 total
                          const upfrontMasks = masks.slice(0, 5); // First 5 masks shown upfront
                          const additionalMasks = generateAdditionalDIYMasks(
                            scan.analysis.skinHealthIndex,
                            scan.analysis.acneAnalysis?.overallSeverity || 'mild',
                            scan.analysis.acneAnalysis?.estimatedPH ?? undefined,
                            scan.analysis.acneAnalysis?.acneType?.inflammatory === 'moderate' || 
                            scan.analysis.acneAnalysis?.acneType?.inflammatory === 'pronounced'
                          ).slice(0, 8); // 8 additional masks
                          allMasks = [...upfrontMasks, ...additionalMasks]; // Total: 13 masks
                        } else {
                          // For Free: Just use the 3 masks that are shown upfront
                          allMasks = masks.slice(0, 3);
                        }
                        
                        // Replace all existing saved masks with the new ones (don't accumulate)
                        const masksToSave = allMasks.map(mask => ({
                          ...mask,
                          scanId: scan.scanId,
                          scanCustomName: scan.customName,
                          savedAt: new Date().toISOString(),
                        }));
                        
                        localStorage.setItem(`savedDIYMasks_${userProfile.uid}`, JSON.stringify(masksToSave));
                        showToast(`${masksToSave.length} DIY masks saved to DIY Masks tab!`, 'success');
                      }
                    }}
                  >
                    <Save size={16} />
                    Save to DIY Masks Tab
                  </Button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
                  {scan.analysis.diyMasks.slice(0, tier === 'plus' ? 5 : 5).map((mask) => (
                    <div key={mask.id} style={{ padding: '1rem', background: theme.colors.background.secondary, borderRadius: theme.borderRadius.md }}>
                      <div style={{ display: 'flex', alignItems: 'start', gap: '0.75rem', marginBottom: '0.5rem' }}>
                        <span style={{ fontSize: '2rem' }}>{mask.emoji}</span>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: '0 0 0.25rem 0', fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold }}>
                            {mask.title}
                          </h4>
                          <p style={{ margin: '0 0 0.5rem 0', fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                            {mask.description}
                          </p>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                            {mask.benefits.map((benefit, i) => (
                              <Badge key={i} variant="info" size="sm">{benefit}</Badge>
                            ))}
                          </div>
                          <details>
                            <summary style={{ cursor: 'pointer', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, color: theme.colors.primary[600] }}>
                              View Recipe & Instructions
                            </summary>
                            <div style={{ marginTop: '0.75rem', paddingLeft: '0.5rem' }}>
                              <div style={{ marginBottom: '0.75rem' }}>
                                <strong style={{ fontSize: theme.typography.fontSize.sm }}>Ingredients:</strong>
                                <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem', fontSize: theme.typography.fontSize.sm }}>
                                  {mask.ingredients.map((ing, i) => (
                                    <li key={i}>{ing}</li>
                                  ))}
                                </ul>
                              </div>
                              <div style={{ marginBottom: '0.75rem' }}>
                                <strong style={{ fontSize: theme.typography.fontSize.sm }}>Instructions:</strong>
                                <ol style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem', fontSize: theme.typography.fontSize.sm }}>
                                  {mask.instructions.map((step, i) => (
                                    <li key={i} style={{ marginBottom: '0.25rem' }}>{step}</li>
                                  ))}
                                </ol>
                              </div>
                              <p style={{ margin: '0.5rem 0 0 0', fontSize: theme.typography.fontSize.xs, color: theme.colors.text.tertiary }}>
                                <strong>Duration:</strong> {mask.duration}
                              </p>
                              <p style={{ margin: '0.5rem 0 0 0', fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary, fontStyle: 'italic' }}>
                                {mask.science}
                              </p>
                            </div>
                          </details>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Show "View More" button for Plus+ users - always show for Plus plan if there are any masks */}
                {((tier === 'plus' && scan.analysis.diyMasks.length > 0) || 
                  (tier !== 'plus' && scan.analysis.diyMasks.length > 5)) && (
                  <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (scan.analysis && scan.scanId) {
                          // Import the function to generate additional masks
                          const { generateAdditionalDIYMasks } = await import('../utils/diyMaskGenerator');
                          
                          // For Plus: generate 5 additional masks (to make 10 total)
                          // For Pro+: generate 8 additional masks
                          const additionalCount = tier === 'plus' ? 5 : 8;
                          const additionalMasks = generateAdditionalDIYMasks(
                            scan.analysis.skinHealthIndex,
                            scan.analysis.acneAnalysis?.overallSeverity || 'mild',
                            scan.analysis.acneAnalysis?.estimatedPH ?? undefined,
                            scan.analysis.acneAnalysis?.acneType?.inflammatory === 'moderate' || 
                            scan.analysis.acneAnalysis?.acneType?.inflammatory === 'pronounced'
                          ).slice(0, additionalCount);
                          
                          navigate('/diy-masks-detail', {
                            state: { 
                              diyMasks: additionalMasks, 
                              scanId: scan.scanId,
                              scanCustomName: scan.customName,
                              totalMasks: tier === 'plus' ? 10 : (tier === 'pro' || tier === 'elite' ? 13 : (scan.analysis.diyMasks?.length || 0) + additionalMasks.length),
                              createdAt: scan.createdAt,
                              scanAnalysis: scan.analysis // Pass full analysis for reference
                            }
                          });
                        }
                      }}
                    >
                      View More DIY Masks ({tier === 'plus' ? '5' : '8'} additional masks)
                      <ArrowRight size={16} style={{ marginLeft: '0.5rem' }} />
                    </Button>
                  </div>
                )}
              </InfoCard>
            )}
          </ColumnContainer>
        </EqualTwoColumnGrid>

        {/* Bottom Row: Next Best Step, Progress Highlights, Recommendations */}
        <AnalysisGrid>
          <InfoCard>
            <CardTitle>
              <ArrowRight aria-hidden="true" />
              Next best step
            </CardTitle>
            {status === 'processing' || !scan.analysis ? (
              <p style={{ margin: 0, color: theme.colors.text.secondary }}>
                Determining next steps...
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <p style={{ margin: 0, color: theme.colors.text.secondary }}>
                  {scan.analysis.nextBestStep}
                </p>
                <Button variant="secondary" onClick={() => navigate('/progress')}>
                  View my progress
                </Button>
              </div>
            )}
          </InfoCard>

          <InfoCard>
            <CardTitle>
              <TrendingUp aria-hidden="true" />
              Progress highlights
            </CardTitle>
            {status === 'processing' || !scan.analysis ? (
              <p style={{ margin: 0, color: theme.colors.text.secondary }}>
                Calculating progress insights...
              </p>
            ) : (
              <List>
                {scan.analysis.progressHighlights.map((highlight, idx) => (
                  <li key={idx}>{highlight}</li>
                ))}
              </List>
            )}
          </InfoCard>

          <InfoCard>
            <CardTitle>
              <Leaf aria-hidden="true" />
              Recommendations
            </CardTitle>
            {status === 'processing' || !scan.analysis ? (
              <p style={{ margin: 0, color: theme.colors.text.secondary }}>
                Generating personalized recommendations...
              </p>
            ) : (
              <List>
                {scan.analysis.recommendations.map((rec, idx) => (
                  <li key={idx}>{rec}</li>
                ))}
              </List>
            )}
          </InfoCard>
        </AnalysisGrid>

        <Card padding="lg">
          <h3 style={{ marginTop: 0 }}>Need a fresh scan?</h3>
          <p style={{ color: theme.colors.text.secondary }}>
            Replace this scan anytime. We'll keep your history so you can spot trends and improvements.
          </p>
          <Button onClick={() => navigate('/scan')}>
            Retake now <ArrowRight size={18} />
          </Button>
        </Card>
        </Container>
      </main>

      {showDeleteSnackbar && (
        <Snackbar role="status">
          <span>Scan deleting in {deleteCountdown}s</span>
          <SnackbarButton onClick={undoDelete}>
            <Undo2 size={16} />
            Undo
          </SnackbarButton>
        </Snackbar>
      )}
    </PageContainer>
  );
};

export default ResultsDetailPage;

